import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const StarRow = ({ value, size = 16 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= Math.round(value) ? "fill-primary text-primary" : "text-black/30"} />
    ))}
  </span>
);

export default function ReviewsSection({ slug }) {
  const [reviews, setReviews] = useState([]);
  const [me, setMe] = useState(null); // null = checking, false = signed out, object = signed in
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/products/${slug}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    api.get("/customer/me").then((r) => setMe(r.data?.customer || false)).catch(() => setMe(false));
    if (window.location.hash === "#reviews") {
      setTimeout(() => document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [slug]);

  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post(`/products/${slug}/reviews`, { rating, text });
      setReviews((prev) => [r.data, ...prev]);
      setText("");
      setRating(5);
      toast.success("Review posted. Loud and proud.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="reviews" className="mt-12 border-t-2 border-black pt-10" data-testid="reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h2 className="font-display uppercase text-3xl tracking-tight">Reviews</h2>
        {reviews.length > 0 && (
          <p className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest" data-testid="reviews-summary">
            <StarRow value={avg} /> {avg.toFixed(1)} · {reviews.length} review{reviews.length === 1 ? "" : "s"}
          </p>
        )}
      </div>

      {me && (
        <form onSubmit={submit} className="mt-8 border-2 border-black p-5 shadow-hard bg-background" data-testid="review-form">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.2em]">
            Go on record as {me.name}
          </p>
          <p className="mt-1 font-mono text-[11px] text-black/50" data-testid="review-verified-note">
            Verified buyers only — reviews unlock once your order for this piece is paid.
          </p>
          <div className="mt-3 flex gap-1" data-testid="review-rating-input">
            {[1, 2, 3, 4, 5].map((i) => (
              <button
                type="button"
                key={i}
                data-testid={`review-star-${i}`}
                onClick={() => setRating(i)}
                className="p-1 hover:scale-110 transition-transform"
                aria-label={`${i} star${i === 1 ? "" : "s"}`}
              >
                <Star size={22} className={i <= rating ? "fill-primary text-primary" : "text-black/30"} />
              </button>
            ))}
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="review-text-input"
            placeholder="How's the fit? How many relatives did it offend?"
            rows={3}
            maxLength={1000}
            className="mt-4 w-full border-2 border-black bg-background px-4 py-3 font-mono text-sm placeholder:text-black/30 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {error && (
            <p className="mt-2 font-mono text-xs font-bold text-red-700" data-testid="review-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={busy || !text.trim()}
            data-testid="review-submit-button"
            className="mt-4 border-2 border-black bg-black px-6 py-3 font-display uppercase text-lg text-background shadow-hard btn-press hover:bg-primary disabled:opacity-50"
          >
            {busy ? "Posting…" : "Post review"}
          </button>
        </form>
      )}

      {me === false && (
        <div className="mt-8 border-2 border-dashed border-black/40 p-5" data-testid="review-signin-prompt">
          <p className="font-mono text-sm text-black/60">
            Bought it? Worn it? Survived a family dinner in it?
          </p>
          <Link
            to="/account"
            data-testid="review-signin-link"
            className="mt-3 inline-block border-2 border-black bg-background px-5 py-2.5 font-display uppercase text-base shadow-hard btn-press hover:bg-accent"
          >
            Sign in to leave a review
          </Link>
        </div>
      )}

      <div className="mt-8 space-y-4">
        {reviews.length === 0 && (
          <p className="font-mono text-sm text-black/50" data-testid="reviews-empty">
            No reviews yet. Be the first to go on record.
          </p>
        )}
        {reviews.map((r) => (
          <article key={r.id} data-testid={`review-${r.id}`} className="border-2 border-black p-4 bg-[#EAEAE4]">
            <div className="flex items-center justify-between gap-3">
              <StarRow value={r.rating} size={14} />
              <p className="font-mono text-[10px] uppercase tracking-widest text-black/40">
                {new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
            </div>
            <p className="mt-2 font-mono text-sm leading-relaxed">{r.text}</p>
            <p className="mt-2 font-mono text-xs font-bold uppercase tracking-widest text-black/50">— {r.name}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
